import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../styles/WelcomeLetterWizard.css';
import DriveFolderPicker from './DriveFolderPicker';

function WelcomeLetterWizard() {
  const [step, setStep] = useState(1);
  const [employees, setEmployees] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    employee_id: '',
    employee_first_name: '',
    employee_last_name: '',
    department: '',
    start_date: '',
    issue_date: new Date().toISOString().split('T')[0],
    template_id: null
  });

  const [preview, setPreview] = useState('');
  const [showDrivePicker, setShowDrivePicker] = useState(false);
  const [savingToDrive, setSavingToDrive] = useState(false);
  const [driveSaved, setDriveSaved] = useState(null);   // { name, link, folder } after a successful save

  useEffect(() => {
    fetchEmployees();
    fetchTemplates();
  }, []);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/welcome-letter/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/welcome-letter/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
      // Set the first template as default
      if (response.data.length > 0) {
        setFormData(prev => ({ ...prev, template_id: response.data[0].id }));
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleEmployeeSelect = (e) => {
    const employeeId = e.target.value;
    const employee = employees.find(emp => emp.id === parseInt(employeeId));
    
    if (employee) {
      setFormData({
        ...formData,
        employee_id: employeeId,
        employee_first_name: employee.first_name,
        employee_last_name: employee.last_name
      });
    } else {
      setFormData({
        ...formData,
        employee_id: ''
      });
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const generatePreview = () => {
    console.log('Generating preview...');
    console.log('Templates:', templates);
    console.log('Form Data:', formData);
    
    // Use the first template if we have templates
    const template = templates.length > 0 ? templates[0] : null;
    
    if (!template) {
      console.error('No templates available!');
      setPreview('Error: No welcome letter template found. Please contact support.');
      return;
    }

    console.log('Using template:', template);

    const first_initial = formData.employee_first_name.charAt(0).toLowerCase();
    
    let preview = template.body
      .replace(/{{employee_first_name}}/g, formData.employee_first_name || '[First Name]')
      .replace(/{{employee_last_name}}/g, formData.employee_last_name || '[Last Name]')
      .replace(/{{start_date}}/g, formData.start_date || '[Start Date]')
      .replace(/{{issue_date}}/g, formData.issue_date || '[Issue Date]')
      .replace(/{{first_initial}}/g, first_initial || '[f]')
      .replace(/{{client_name}}/g, 'California African American Museum');
    
    // Convert to HTML with line breaks
    preview = preview.replace(/\n/g, '<br>');
    
    console.log('Generated preview length:', preview.length);
    setPreview(preview);
  };

  const nextStep = () => {
    if (step === 1 && (!formData.employee_first_name || !formData.employee_last_name || !formData.department)) {
      alert('Please enter first name, last name, and department');
      return;
    }
    if (step === 2 && (!formData.start_date || !formData.issue_date)) {
      alert('Please enter both dates');
      return;
    }
    if (step === 2) {
      generatePreview();
    }
    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
    setSuccess(false);
  };

  const sendWelcomeLetter = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      
      // Convert HTML back to plain text for sending
      const plainTextMessage = preview
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<p>/gi, '')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]*>/g, '');
      
      await axios.post(
        `${process.env.REACT_APP_API_URL}/welcome-letter/send`,
        {
          ...formData,
          message: plainTextMessage
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(true);
      setStep(4);
    } catch (error) {
      console.error('Error sending welcome letter:', error);
      alert('Failed to send welcome letter. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Save the composed letter into a chosen Google Drive folder as an editable Google Doc.
  const saveLetterToDrive = async (folder) => {
    setShowDrivePicker(false);
    setSavingToDrive(true);
    setDriveSaved(null);
    try {
      const token = localStorage.getItem('token');
      const name = `Welcome Letter - ${formData.employee_first_name} ${formData.employee_last_name}`.trim()
        + (formData.issue_date ? ` (${formData.issue_date})` : '');
      const html = `<html><body style="font-family:Georgia,'Times New Roman',serif;font-size:12pt;line-height:1.6;color:#111">${preview}</body></html>`;
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/google/drive/save-doc`,
        { name, html, folderId: folder.folderId, driveId: folder.driveId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDriveSaved({ name, link: res.data.webViewLink, folder: folder.path || folder.name });
    } catch (error) {
      console.error('Error saving letter to Drive:', error);
      alert('Could not save to Google Drive. Make sure your Google account is connected, then try again.');
    } finally {
      setSavingToDrive(false);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setSuccess(false);
    setDriveSaved(null);
    setShowDrivePicker(false);
    setFormData({
      employee_id: '',
      employee_first_name: '',
      employee_last_name: '',
      department: '',
      start_date: '',
      issue_date: new Date().toISOString().split('T')[0],
      template_id: templates.length > 0 ? templates[0].id : null
    });
    setPreview('');
  };

  return (
    <div className="welcome-letter-wizard">
      <div className="wizard-header">
        <h2>Issue Welcome Letter</h2>
        <p>Send a personalized welcome letter to a new employee</p>
      </div>

      <div className="wizard-progress">
        <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
          <div className="progress-circle">1</div>
          <span>Employee Info</span>
        </div>
        <div className={`progress-line ${step > 1 ? 'completed' : ''}`}></div>
        <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
          <div className="progress-circle">2</div>
          <span>Enter Dates</span>
        </div>
        <div className={`progress-line ${step > 2 ? 'completed' : ''}`}></div>
        <div className={`progress-step ${step >= 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`}>
          <div className="progress-circle">3</div>
          <span>Review & Edit</span>
        </div>
      </div>

      <div className="wizard-content">
        {step === 1 && (
          <div className="wizard-step">
            <h3>Step 1: New Employee Information</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  name="employee_first_name"
                  value={formData.employee_first_name}
                  onChange={handleInputChange}
                  placeholder="Enter first name"
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  name="employee_last_name"
                  value={formData.employee_last_name}
                  onChange={handleInputChange}
                  placeholder="Enter last name"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Department *</label>
              <select
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                required
              >
                <option value="">-- Select Department --</option>
                <option value="Administration">Administration</option>
                <option value="Collections">Collections</option>
                <option value="Exhibitions">Exhibitions</option>
                <option value="Education">Education</option>
                <option value="Development">Development</option>
                <option value="Marketing">Marketing</option>
                <option value="Finance">Finance</option>
                <option value="Facilities">Facilities</option>
                <option value="Information Technology">Information Technology</option>
                <option value="Human Resources">Human Resources</option>
              </select>
            </div>

            <div className="form-divider">
              <span>OR SELECT EXISTING EMPLOYEE</span>
            </div>

            <div className="form-group">
              <label>Select Existing Employee (Optional)</label>
              <select
                value={formData.employee_id}
                onChange={handleEmployeeSelect}
              >
                <option value="">-- Select Employee --</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name} ({emp.email})
                  </option>
                ))}
              </select>
              <small style={{fontSize: '12px', color: '#64748b', marginTop: '4px', display: 'block'}}>
                Use this if re-sending a welcome letter to an existing employee
              </small>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="wizard-step">
            <h3>Step 2: Enter Dates</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Issue Date</label>
                <input
                  type="date"
                  name="issue_date"
                  value={formData.issue_date}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="employee-summary">
              <h4>Welcome Letter For:</h4>
              <p><strong>{formData.employee_first_name} {formData.employee_last_name}</strong></p>
              <p>Department: {formData.department || 'Not set'}</p>
              <p>Starting on: {formData.start_date || 'Not set'}</p>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="wizard-step">
            <h3>Step 3: Review & Edit Letter</h3>
            <div className="letter-preview">
              <div className="preview-header">
                <h4>Welcome Letter</h4>
                <p>Edit the letter as needed before sending</p>
              </div>
              <div className="editor-container">
                <ReactQuill
                  theme="snow"
                  value={preview}
                  onChange={setPreview}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      [{ 'align': [] }],
                      ['clean']
                    ]
                  }}
                  style={{ height: '400px', marginBottom: '50px' }}
                />
              </div>
            </div>
            {driveSaved && (
              <div style={{ marginTop: 12, padding: '10px 14px', background: '#ECFDF5', border: '1px solid #B6E6C9', borderRadius: 8, fontSize: 13, color: '#065F46' }}>
                ✓ Saved <strong>{driveSaved.name}</strong> to Google Drive{driveSaved.folder ? <> in <strong>{driveSaved.folder}</strong></> : ''}.{' '}
                {driveSaved.link && <a href={driveSaved.link} target="_blank" rel="noreferrer" style={{ color: '#065F46', fontWeight: 700 }}>Open in Drive →</a>}
              </div>
            )}
          </div>
        )}

        {step === 4 && success && (
          <div className="wizard-step success-step">
            <div className="success-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h3>Welcome Letter Sent!</h3>
            <p>The welcome letter has been delivered to <strong>{formData.employee_first_name} {formData.employee_last_name}</strong>'s inbox.</p>
            <button className="btn-primary" onClick={resetWizard}>Send Another Letter</button>
          </div>
        )}
      </div>

      {step < 4 && (
        <div className="wizard-actions">
          {step > 1 && (
            <button className="btn-cancel" onClick={prevStep}>
              Back
            </button>
          )}
          {step < 3 && (
            <button className="btn-submit" onClick={nextStep}>
              Next
            </button>
          )}
          {step === 3 && (
            <>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => { setDriveSaved(null); setShowDrivePicker(true); }}
                disabled={savingToDrive || sending}
              >
                {savingToDrive ? 'Saving to Drive…' : 'Save to Google Drive'}
              </button>
              <button
                className="btn-submit"
                onClick={sendWelcomeLetter}
                disabled={sending}
              >
                {sending ? 'Sending...' : 'Send Welcome Letter'}
              </button>
            </>
          )}
        </div>
      )}

      {showDrivePicker && (
        <DriveFolderPicker
          saveLabel="Save letter to"
          onCancel={() => setShowDrivePicker(false)}
          onSelect={saveLetterToDrive}
        />
      )}
    </div>
  );
}

export default WelcomeLetterWizard;